import { FC, useEffect, useState } from 'react'
import { useDispatch } from 'react-redux'
import { useNavigate } from 'react-router-dom'
import jwtDecode from 'jwt-decode'
import { Form, Formik } from 'formik'
import * as Yup from 'yup'
import { setUser } from 'ducks/user'
import { setAuth } from 'ducks/auth'
import { setAuthenticatedSession } from 'services/login/auth-session'
import { useNotification } from 'lib/context/notification.context'
import Logo from '../../components/UI/Logo'
import SecurityTransition from '../../components/Forms/Login/SecurityTransition'
import { Button } from '../../components/UI/Buttons'
import Input from '../../components/UI/TextInput'
import { H2 } from '../../components/UI/Text'
import Separator from 'views/components/UI/Separator/Index'
import {
  ActionsRow,
  Container,
  ErrorText,
  ForgotPassword,
  FormBody,
  FormField,
  MfaInstructions,
  MfaRememberDevice,
  MfaStatusAction,
  MfaStatusItem,
  MfaStatusRow,
  SecondaryAction,
} from '../../components/Forms/Login/styled'
import verifyMfa from 'services/login/verifyMfa'
import resendMfa from 'services/login/resendMfa'
import { IMfaChallengeSession, MFA_LOGIN_STORAGE_KEY } from 'services/login/mfa-session'

const MFA_RESEND_MINIMUM_SECONDS = 60
const MINIMUM_MFA_TRANSITION_MS = 1500

interface IMfaFormValues {
  code: string
  rememberDevice: boolean
}

const waitForMfaTransition = async (startedAtMs: number): Promise<void> => {
  const remainingMs = Math.max(0, MINIMUM_MFA_TRANSITION_MS - (Date.now() - startedAtMs))

  if (remainingMs > 0) {
    await new Promise<void>((resolve) => window.setTimeout(resolve, remainingMs))
  }
}

type RoleType = 'special_admin' | 'super_admin' | 'platform_admin' | 'super_platform_admin' | 'user'

const SignInSchema = Yup.object().shape({
  code: Yup.string()
    .matches(/^\d{6}$/, 'El código debe tener 6 dígitos')
    .required('El código MFA es requerido'),
})

const LoginMfaContainer: FC = () => {
  const dispatch = useDispatch()
  const navigate = useNavigate()
  const { getError, getSuccess, getWarning } = useNotification()

  const [challenge, setChallenge] = useState<IMfaChallengeSession | null>(null)
  const [secondsUntilResend, setSecondsUntilResend] = useState<number>(0)
  const [secondsUntilExpiration, setSecondsUntilExpiration] = useState<number>(0)
  const [attemptsLeft, setAttemptsLeft] = useState<number>(5)
  const [loadingVerify, setLoadingVerify] = useState<boolean>(false)
  const [loadingResend, setLoadingResend] = useState<boolean>(false)

  useEffect(() => {
    const storedChallenge = sessionStorage.getItem(MFA_LOGIN_STORAGE_KEY)

    if (!storedChallenge) {
      navigate('/login', { replace: true })
      return
    }

    try {
      const parsedChallenge: IMfaChallengeSession = JSON.parse(storedChallenge) as IMfaChallengeSession
      setChallenge(parsedChallenge)
      setAttemptsLeft(parsedChallenge.attemptsLeft ?? 5)
    } catch {
      sessionStorage.removeItem(MFA_LOGIN_STORAGE_KEY)
      navigate('/login', { replace: true })
    }
  }, [navigate])

  useEffect(() => {
    if (!challenge) {
      return
    }

    const syncTimers = () => {
      const sentAt = new Date(challenge.sentAt).getTime()
      const elapsedSeconds = Math.floor((Date.now() - sentAt) / 1000)
      const resendRemaining = Math.max(0, MFA_RESEND_MINIMUM_SECONDS - elapsedSeconds)
      const expirationRemaining = Math.max(0, challenge.expiresInMinutes * 60 - elapsedSeconds)

      setSecondsUntilResend(resendRemaining)
      setSecondsUntilExpiration(expirationRemaining)
    }

    syncTimers()
    const timerId = window.setInterval(syncTimers, 1000)

    return () => {
      window.clearInterval(timerId)
    }
  }, [challenge])

  const codeValidityLabel = challenge
    ? (() => {
        const remainingMinutes = Math.floor(secondsUntilExpiration / 60)
        const remainingSecondsPart = secondsUntilExpiration % 60

        return `${remainingMinutes}m ${remainingSecondsPart.toString().padStart(2, '0')}s`
      })()
    : ''

  const persistChallenge = (nextChallenge: IMfaChallengeSession) => {
    setChallenge(nextChallenge)
    setAttemptsLeft(nextChallenge.attemptsLeft)
    sessionStorage.setItem(MFA_LOGIN_STORAGE_KEY, JSON.stringify(nextChallenge))
  }

  const handleSubmit = async (values: IMfaFormValues) => {
    if (!challenge) {
      navigate('/login', { replace: true })
      return
    }

    const verificationStartedAtMs: number = Date.now()
    setLoadingVerify(true)

    try {
      const response = await verifyMfa({
        challengeToken: challenge.challengeToken,
        code: values.code.trim(),
        rememberDevice: values.rememberDevice && challenge.canRememberDevice,
      })
      await waitForMfaTransition(verificationStartedAtMs)
      const user = response.data
      const decodedToken: { roleType?: RoleType } = jwtDecode(user.token)
      setAuthenticatedSession(user.token, user.csrf_token)

      dispatch(
        setUser({
          token: user.token,
          email: challenge.email,
          companyId: user.company_id || challenge.companyId || 0,
          userName: user.user_name || challenge.userName || '',
          hasAdminPlatformManagement: false,
          hasQuestionnaireManagement: false,
        })
      )

      dispatch(
        setAuth({
          role: decodedToken.roleType || '',
          isAuth: true,
        })
      )
      sessionStorage.removeItem(MFA_LOGIN_STORAGE_KEY)
      navigate('/', { replace: true })
    } catch (error: any) {
      await waitForMfaTransition(verificationStartedAtMs)
      const status = error?.response?.status

      if (status === 401) {
        const nextAttempts = Math.max(0, attemptsLeft - 1)
        setAttemptsLeft(nextAttempts)
        persistChallenge({
          ...challenge,
          attemptsLeft: nextAttempts,
        })
        getWarning('El código MFA es incorrecto')
        return
      }

      if (status === 423) {
        sessionStorage.removeItem(MFA_LOGIN_STORAGE_KEY)
        getWarning('El código MFA expiró. Vuelve a iniciar sesión')
        navigate('/login', { replace: true })
        return
      }

      if (status === 429) {
        sessionStorage.removeItem(MFA_LOGIN_STORAGE_KEY)
        getWarning('Superaste el límite de validaciones permitidas')
        navigate('/login', { replace: true })
        return
      }

      getError('No fue posible validar el código MFA')
    } finally {
      setLoadingVerify(false)
    }
  }

  const handleResend = async () => {
    if (!challenge || secondsUntilResend > 0 || challenge.resendCount >= 3) {
      return
    }

    setLoadingResend(true)

    try {
      const response = await resendMfa({
        challengeToken: challenge.challengeToken,
      })
      const nextChallenge: IMfaChallengeSession = {
        ...challenge,
        sentAt: new Date().toISOString(),
        resendCount: response.data?.resend_count ?? challenge.resendCount + 1,
        attemptsLeft: 5,
      }

      persistChallenge(nextChallenge)
      getSuccess('Código reenviado')
    } catch (error: any) {
      const status = error?.response?.status

      if (status === 423) {
        sessionStorage.removeItem(MFA_LOGIN_STORAGE_KEY)
        getWarning('El código MFA expiró. Vuelve a iniciar sesión')
        navigate('/login', { replace: true })
        return
      }

      if (status === 429) {
        getWarning('Aún no puedes reenviar el código o ya alcanzaste el límite')
        return
      }

      if (status === 401) {
        getWarning('El challenge MFA no es válido')
        return
      }

      getError('No fue posible reenviar el código MFA')
    } finally {
      setLoadingResend(false)
    }
  }

  const handleCancel = () => {
    sessionStorage.removeItem(MFA_LOGIN_STORAGE_KEY)
    navigate('/login', { replace: true })
  }

  if (!challenge) {
    return null
  }

  return (
    <Container>
      <SecurityTransition isLoading={loadingVerify} isMfa isPersistent>
      <Logo width="245px" />
      <Separator height="1rem" />
      <H2 sx={{ width: 420, margin: '0px 0px 12px 0px', alignSelf: 'center', textAlign: 'center' }}>
        Verifica tu acceso
      </H2>
      <MfaInstructions>
        <span>Hemos enviado un código de 6 dígitos.</span>
        <span>
          Revísalo en <strong>{challenge.email}</strong> e introdúcelo para continuar.
        </span>
      </MfaInstructions>
      <Separator height="1.5rem" />
      <Formik<IMfaFormValues>
        validationSchema={SignInSchema}
        onSubmit={handleSubmit}
        initialValues={{ code: '', rememberDevice: false }}
      >
        {({ errors, touched, values, handleChange, isValid }) => (
          <Form style={{ width: '100%' }}>
            <FormBody>
              <FormField>
                <Input
                  value={values.code}
                  onChange={handleChange('code')}
                  type="text"
                  name="code"
                  label="Código MFA"
                  inputProps={{ maxLength: 6, inputMode: 'numeric' }}
                />
                {errors.code && touched.code ? (
                  <div>
                    <ErrorText>{errors.code}</ErrorText>
                  </div>
                ) : null}
              </FormField>
              <MfaStatusRow>
                <MfaStatusItem>
                  <span>Intentos restantes</span>
                  <strong>{attemptsLeft}</strong>
                </MfaStatusItem>
                <MfaStatusItem>
                  <span>El código expira en</span>
                  <strong>{codeValidityLabel}</strong>
                </MfaStatusItem>
                <MfaStatusAction
                  type="button"
                  disabled={secondsUntilResend > 0 || challenge.resendCount >= 3 || loadingResend}
                  onClick={handleResend}
                >
                  <span>
                    {challenge.resendCount >= 3 ? 'Reenvíos' : secondsUntilResend > 0 ? 'Reenviar en' : 'Reenviar código'}
                  </span>
                  <strong>
                    {loadingResend
                      ? 'Enviando...'
                      : challenge.resendCount >= 3
                      ? 'Límite alcanzado'
                      : secondsUntilResend > 0
                      ? `${secondsUntilResend}s`
                      : 'Disponible'}
                  </strong>
                </MfaStatusAction>
              </MfaStatusRow>
              {challenge.canRememberDevice ? (
                <MfaRememberDevice>
                  <input
                    type="checkbox"
                    name="rememberDevice"
                    checked={values.rememberDevice}
                    onChange={handleChange('rememberDevice')}
                  />
                  Recordar este dispositivo durante 30 dias
                </MfaRememberDevice>
              ) : null}
              <ActionsRow>
                <SecondaryAction>
                  <ForgotPassword onClick={handleCancel}>Usar otra cuenta</ForgotPassword>
                </SecondaryAction>
                <Button fullWidth disabled={!isValid || loadingVerify} type="submit" size="lg">
                  {loadingVerify ? 'Validando...' : 'Validar código'}
                </Button>
              </ActionsRow>
            </FormBody>
          </Form>
        )}
      </Formik>
      </SecurityTransition>
    </Container>
  )
}

export default LoginMfaContainer
